/**
 * Regras puras do formulário de orçamento: máscara, validação e a mensagem que
 * vai para o WhatsApp.
 *
 * Separadas do componente por dois motivos. Exportar função de um arquivo que
 * também exporta componente quebra o Fast Refresh do Vite — o lint avisa. E
 * são justamente as partes com casos de borda: o que fica no texto quando um
 * campo opcional vem vazio, e como um número colado já formatado é entendido.
 */

export interface DadosDoOrcamento {
  largura: string;
  altura: string;
  prazo: string;
  observacao: string;
  nome: string;
  telefone: string;
  cidade: string;
  email: string;
}

/**
 * Máscara de telefone, portada do protótipo.
 *
 * Formata enquanto digita e aceita fixo (10 dígitos) e celular (11), sem
 * travar o campo: quem colar um número já formatado continua sendo entendido,
 * porque só os dígitos importam.
 */
export function formatarTelefone(valor: string): string {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);

  if (digitos.length > 10) {
    return digitos.replace(/(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3');
  }

  return digitos.replace(
    /(\d{0,2})(\d{0,4})(\d{0,4})/,
    (_, ddd: string, meio: string, fim: string) =>
      [ddd && `(${ddd}`, ddd.length === 2 ? ') ' : '', meio, fim && `-${fim}`].join(''),
  );
}

/** Dez dígitos é o mínimo de um número brasileiro com DDD. */
export function telefoneValido(valor: string): boolean {
  return valor.replace(/\D/g, '').length >= 10;
}

export function nomeValido(valor: string): boolean {
  return valor.trim().length > 1;
}

/**
 * Monta a mensagem do WhatsApp, na ordem e no formato do protótipo.
 *
 * Uma diferença consciente: as linhas em branco que separam cabeçalho, medidas
 * e contato sobrevivem. No protótipo elas eram escritas e logo apagadas pelo
 * `.filter(Boolean)` do final, junto com os campos vazios — string vazia é
 * falsa. Quem as escreveu queria separar os blocos, e a mensagem que chega no
 * celular da marcenaria fica legível com elas. Campo omitido vira `null`, que
 * é o que a filtragem tira.
 */
export function montarMensagem(
  dados: DadosDoOrcamento,
  ambientes: readonly string[],
  empresa: string,
): string {
  const linhas: (string | null)[] = [
    `*Orçamento pelo site — ${empresa}*`,
    '',
    `*Ambientes:* ${ambientes.join(', ') || 'a combinar'}`,
    dados.largura.trim() ? `*Largura:* ${dados.largura.trim()} m` : null,
    dados.altura.trim() ? `*Pé-direito:* ${dados.altura.trim()} m` : null,
    `*Prazo:* ${dados.prazo}`,
    dados.observacao.trim() ? `*Detalhes:* ${dados.observacao.trim()}` : null,
    '',
    `*Nome:* ${dados.nome.trim()}`,
    `*WhatsApp:* ${dados.telefone}`,
    dados.cidade.trim() ? `*Cidade:* ${dados.cidade.trim()}` : null,
    dados.email.trim() ? `*E-mail:* ${dados.email.trim()}` : null,
  ];

  return linhas.filter((linha) => linha !== null).join('\n');
}
